import { fetchDelete, get, patch, post } from "./fetchUtils.js";

const CDES_URL = (import.meta.env.VITE_CDES_URL || "").replace(/\/+$/, "");

const cdesGet = (endpoint, queryParams) => {
    if (!CDES_URL) {
        throw new Error("VITE_CDES_URL is not configured.");
    }
    return get({ url: CDES_URL, endpoint, queryParams });
};

const visibilityParams = ({ includeInactiveClients, includeInactivePlans } = {}) => ({
    includeInactiveClients:
        includeInactiveClients === undefined ? undefined : String(includeInactiveClients),
    includeInactivePlans: includeInactivePlans === undefined ? undefined : String(includeInactivePlans),
});

const listPlansPage = ({
    clientId,
    cursor,
    limit = 50,
    includeInactiveClients,
    includeInactivePlans,
} = {}) =>
    cdesGet("/plans", {
        clientId: clientId || undefined,
        cursor: cursor || undefined,
        limit: clientId ? undefined : limit,
        ...visibilityParams({ includeInactiveClients, includeInactivePlans }),
    });

const listAllPlans = async ({ clientId, includeInactiveClients, includeInactivePlans } = {}) => {
    const plans = [];
    let cursor;
    do {
        const page = await listPlansPage({
            clientId,
            cursor,
            includeInactiveClients,
            includeInactivePlans,
        });
        plans.push(...(page.plans ?? []));
        cursor = page.nextCursor;
    } while (cursor);
    return plans;
};

const listClientsPage = ({ cursor, limit = 50, includeInactive = false } = {}) =>
    cdesGet("/clients", {
        cursor: cursor || undefined,
        limit,
        includeInactive: includeInactive ? "true" : undefined,
    });

const listAllClients = async ({ includeInactive = false } = {}) => {
    const clients = [];
    let cursor;
    do {
        const page = await listClientsPage({ cursor, limit: 50, includeInactive });
        clients.push(...(page.clients ?? []));
        cursor = page.nextCursor;
    } while (cursor);
    return clients;
};

const getClient = (clientId) => cdesGet(`/clients/${encodeURIComponent(clientId)}`);

const clientPath = (clientId) => `/clients/${encodeURIComponent(clientId)}`;

const mapClientWriteError = (error) => {
    if (error?.message === "Client already exists") {
        return new Error("A client with this Client ID already exists.");
    }
    return error;
};

const mapPlanWriteError = (error) => {
    if (error?.message === "Plan already exists") {
        return new Error("A plan with this Plan ID already exists for this client.");
    }
    return error;
};

const guardedWrite = async (request, mapError) => {
    if (!CDES_URL) {
        throw new Error("VITE_CDES_URL is not configured.");
    }
    try {
        return await request();
    } catch (error) {
        throw mapError(error);
    }
};

const cdesWrite = (request) => guardedWrite(request, mapClientWriteError);

const createClient = (body) =>
    cdesWrite(() => post({ url: CDES_URL, endpoint: "/clients", body }));

const updateClient = (clientId, body) =>
    cdesWrite(() => patch({ url: CDES_URL, endpoint: clientPath(clientId), body }));

const deactivateClient = (clientId) =>
    cdesWrite(() => fetchDelete({ url: CDES_URL, endpoint: clientPath(clientId) }));

const deleteClient = (clientId) =>
    cdesWrite(() => fetchDelete({ url: CDES_URL, endpoint: `${clientPath(clientId)}/permanent` }));

const planWrite = (request) => guardedWrite(request, mapPlanWriteError);

const planPath = (clientId, planId) =>
    `/plans/${encodeURIComponent(clientId)}/${encodeURIComponent(planId)}`;

const createPlan = (body) =>
    planWrite(() => post({ url: CDES_URL, endpoint: "/plans", body }));

const updatePlan = (clientId, planId, body) =>
    planWrite(() => patch({ url: CDES_URL, endpoint: planPath(clientId, planId), body }));

const deactivatePlan = (clientId, planId) =>
    planWrite(() => fetchDelete({ url: CDES_URL, endpoint: planPath(clientId, planId) }));

const deletePlan = (clientId, planId) =>
    planWrite(() => fetchDelete({ url: CDES_URL, endpoint: `${planPath(clientId, planId)}/permanent` }));

const mapFieldWriteError = (error) => {
    if (error?.message === "Field already exists") {
        return new Error("A field with this key already exists.");
    }
    return error;
};

const fieldWrite = (request) => guardedWrite(request, mapFieldWriteError);

const fieldPath = (fieldKey) => `/fields/${encodeURIComponent(fieldKey)}`;

const listFields = (filters = {}) => {
    const queryParams = {};
    if (filters.category) queryParams.category = filters.category;
    if (filters.dataType) queryParams.dataType = filters.dataType;
    if (filters.scope) queryParams.scope = filters.scope;
    if (filters.universal === "true" || filters.universal === "false") queryParams.universal = filters.universal;
    if (filters.documentType) queryParams.documentType = filters.documentType;
    if (filters.includeInactive) queryParams.includeInactive = "true";
    if (typeof filters.q === "string" && filters.q.trim()) queryParams.q = filters.q.trim();
    return cdesGet("/fields", queryParams);
};

const listDocumentTypes = () => cdesGet("/document-types");

const createField = (body) => fieldWrite(() => post({ url: CDES_URL, endpoint: "/fields", body }));

const updateField = (fieldKey, body) =>
    fieldWrite(() => patch({ url: CDES_URL, endpoint: fieldPath(fieldKey), body }));

const deactivateField = (fieldKey) =>
    fieldWrite(() => fetchDelete({ url: CDES_URL, endpoint: fieldPath(fieldKey) }));

const deleteField = (fieldKey) =>
    fieldWrite(() => fetchDelete({ url: CDES_URL, endpoint: `${fieldPath(fieldKey)}/permanent` }));

const exportFieldCatalog = () => cdesGet("/fields/export");

const previewFieldImport = (body) =>
    fieldWrite(() => post({ url: CDES_URL, endpoint: "/fields/import/preview", body }));

const applyFieldImport = (body) =>
    fieldWrite(() => post({ url: CDES_URL, endpoint: "/fields/import", body: { ...body, apply: true } }));

export {
    cdesGet,
    listPlansPage,
    listAllPlans,
    listClientsPage,
    listAllClients,
    getClient,
    createClient,
    updateClient,
    deactivateClient,
    deleteClient,
    createPlan,
    updatePlan,
    deactivatePlan,
    deletePlan,
    listFields,
    listDocumentTypes,
    createField,
    updateField,
    deactivateField,
    deleteField,
    exportFieldCatalog,
    previewFieldImport,
    applyFieldImport,
    mapClientWriteError,
    mapPlanWriteError,
    mapFieldWriteError,
};
